/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * DAS Topology Constants
 * Colors, dimensions, and configuration from UX mockup
 */

import { DasNodeType, NodeTypeConfig, LayoutConfig, DasNodeStatus } from "./types";

// Layout configuration (from mockup - horizontal left-to-right layout)
export const LAYOUT_CONFIG: LayoutConfig = {
  xGap: 120,  // Horizontal spacing between hierarchy levels
  yGap: 4,   // Vertical spacing between sibling nodes
  nodeWidth: 48,  // Base width for calculations
  nodeHeight: 24, // Base height for calculations
};

// Status colors
export const STATUS_COLORS: Record<DasNodeStatus, string> = {
  [DasNodeStatus.ONLINE]: "#22c55e",      // green-500
  [DasNodeStatus.MINOR]: "#eab308",       // yellow-500
  [DasNodeStatus.MAJOR]: "#f97316",       // orange-500
  [DasNodeStatus.CRITICAL]: "#ef4444",    // red-500
  [DasNodeStatus.DISCONNECTED]: "#6b7280", // gray-500
  [DasNodeStatus.UNKNOWN]: "#9ca3af",     // gray-400
  [DasNodeStatus.CLEAR]: "#22c55e",       // green-500 (same as online)
};

// Node type configurations (matching mockup exact dimensions)
export const NODE_TYPE_CONFIG: Record<DasNodeType, NodeTypeConfig> = {
  [DasNodeType.SITE]: {
    color: "#334155",      // slate-700 (mockup uses consistent fill)
    glowColor: "#60a5fa",  // blue-400
    shape: "roundedRect",
    width: 100,  // Wider to accommodate MA6200-A2 and MA-3000 labels
    height: 20,  // mockup: height={20}
    radius: 4,   // mockup: rx={4}
  },
  [DasNodeType.HEU]: {
    color: "#334155",      // slate-700
    glowColor: "#a78bfa",  // violet-400
    shape: "roundedRect",
    width: 58,   // 20% wider
    height: 24,  // mockup: height={24}
    radius: 3,   // mockup: rx={3}
  },
  [DasNodeType.OIU]: {
    color: "#334155",      // slate-700
    glowColor: "#a78bfa",  // violet-400
    shape: "roundedRect",
    width: 58,   // 20% wider
    height: 24,  // mockup: height={24}
    radius: 3,   // mockup: rx={3}
  },
  [DasNodeType.IHU]: {
    color: "#334155",      // slate-700
    glowColor: "#a78bfa",  // violet-400
    shape: "roundedRect",
    width: 58,   // 20% wider
    height: 24,  // mockup: height={24}
    radius: 3,   // mockup: rx={3}
  },
  [DasNodeType.ACM]: {
    color: "#334155",      // slate-700
    glowColor: "#22d3ee",  // cyan-400
    shape: "rect",
    width: 43,   // 20% wider
    height: 16,  // mockup: height={16}
    radius: 4,   // mockup: rx={4}
  },
  [DasNodeType.RIM]: {
    color: "#334155",      // slate-700
    glowColor: "#34d399",  // emerald-400
    shape: "rect",
    width: 70,   // Wider to accommodate band label (RIM-LTE-5)
    height: 16,  // mockup: height={16}
    radius: 4,   // mockup: rx={4}
  },
  [DasNodeType.OIM]: {
    color: "#334155",      // slate-700
    glowColor: "#fbbf24",  // amber-400
    shape: "rect",
    width: 43,   // 20% wider
    height: 16,  // mockup: height={16}
    radius: 4,   // mockup: rx={4}
  },
  [DasNodeType.OCH]: {
    color: "#334155",      // slate-700
    glowColor: "#10b981",  // green-500
    shape: "rect",
    width: 43,   // 20% wider
    height: 16,  // mockup: height={16}
    radius: 4,   // mockup: rx={4}
  },
  [DasNodeType.GX]: {
    color: "#334155",      // slate-700
    glowColor: "#8b5cf6",  // violet-500
    shape: "rect",
    width: 43,   // 20% wider
    height: 16,  // mockup: height={16}
    radius: 4,   // mockup: rx={4}
  },
  [DasNodeType.FMM]: {
    color: "#334155",      // slate-700
    glowColor: "#f472b6",  // pink-400
    shape: "rect",
    width: 43,   // 20% wider
    height: 16,  // mockup: height={16}
    radius: 4,   // mockup: rx={4}
  },
  [DasNodeType.RAU]: {
    color: "#334155",      // slate-700
    glowColor: "#818cf8",  // indigo-400
    shape: "circle",
    width: 30,   // Increased for better visibility
    height: 30,
    radius: 15,  // Increased radius
  },
  [DasNodeType.MRU]: {
    color: "#334155",      // slate-700
    glowColor: "#a78bfa",  // violet-400
    shape: "circle",
    width: 30,   // Increased for better visibility
    height: 30,
    radius: 15,  // Increased radius
  },
  [DasNodeType.RXU]: {
    color: "#334155",      // slate-700
    glowColor: "#2dd4bf",  // teal-400
    shape: "circle",
    width: 30,   // Increased for better visibility
    height: 30,
    radius: 15,  // Increased radius
  },
  [DasNodeType.FCM]: {
    color: "#334155",      // slate-700
    glowColor: "#fb923c",  // orange-400
    shape: "rect",
    width: 43,   // 20% wider
    height: 16,  // mockup: height={16}
    radius: 4,   // mockup: rx={4}
  },
  [DasNodeType.MXU]: {
    color: "#334155",      // slate-700
    glowColor: "#c084fc",  // purple-400
    shape: "circle",
    width: 30,   // Increased for better visibility
    height: 30,
    radius: 15,  // Increased radius
  },
  [DasNodeType.DCU]: {
    color: "#334155",      // slate-700
    glowColor: "#38bdf8",  // sky-400
    shape: "rect",
    width: 43,
    height: 16,
    radius: 4,
  },
  [DasNodeType.DEU]: {
    color: "#334155",      // slate-700
    glowColor: "#22d3ee",  // cyan-400
    shape: "rect",
    width: 43,
    height: 16,
    radius: 4,
  },
  [DasNodeType.RIU]: {
    color: "#334155",      // slate-700
    glowColor: "#4ade80",  // green-400
    shape: "rect",
    width: 43,
    height: 16,
    radius: 4,
  },
  [DasNodeType.DXRU]: {
    color: "#334155",      // slate-700
    glowColor: "#60a5fa",  // blue-400
    shape: "circle",
    width: 30,
    height: 30,
    radius: 15,
  },
  [DasNodeType.DXRU10G]: {
    color: "#334155",      // slate-700
    glowColor: "#818cf8",  // indigo-400
    shape: "circle",
    width: 30,
    height: 30,
    radius: 15,
  },
};

// Equipment type to DasNodeType mapping
export const EQUIPMENT_TYPE_MAP: Record<string, DasNodeType> = {
  "HEU": DasNodeType.HEU,
  "OIU": DasNodeType.OIU,
  "IHU": DasNodeType.IHU,
  "ACM": DasNodeType.ACM,
  "RIM": DasNodeType.RIM,
  "OIM": DasNodeType.OIM,
  "OCH": DasNodeType.OCH,
  "GX": DasNodeType.GX,
  "FMM": DasNodeType.FMM,
  "FRM": DasNodeType.FMM, // FRM (Fiber Remote Module) uses same type as FMM
  "RAU": DasNodeType.RAU,
  "MRU": DasNodeType.MRU,
  "RXU": DasNodeType.RXU,
  "FCM": DasNodeType.FCM,
  "MXU": DasNodeType.MXU,
  "DCU": DasNodeType.DCU,
  "DEU": DasNodeType.DEU,
  "RIU": DasNodeType.RIU,
  "DXRU": DasNodeType.DXRU,
  "DXRU10G": DasNodeType.DXRU10G,
};
